import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, MessageCircle, Mail, Zap, ArrowRight, Send, Loader2, CheckCircle2,
  ShieldCheck, Award, Clock, Globe2, Building2, Sparkles, ChevronDown, MapPin,
  Lock, CreditCard, BadgeCheck,
} from "lucide-react";
import { Link, useNavigate } from "react-router-dom";
import { useLang } from "../i18n/LangProvider";
import { Reveal } from "../components/Reveal";
import { SophieConcierge } from "../components/SophieConcierge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PHONE = "+41774069813";
const PHONE_DISPLAY = "+41 77 406 98 13";
const WA_URL = `https://wa.me/${PHONE.replace(/[^0-9]/g, "")}`;
const EMAIL = "info@swissalpenline.ch";

const IMG_HERO = "/images/byd-lucerne-hero.jpg";
const GOLD = "#B08D2F";

// Reference generator (client-side; server will re-issue on real booking)
const genRef = () => `SAL-2026-${Math.floor(Math.random() * 900000 + 100000)}`;

const T = {
  de: {
    kicker: "Kontakt · 09:00 – 19:30",
    title: "Lassen Sie uns Ihre Fahrt planen.",
    subtitle: "Ob Flughafentransfer, Executive Chauffeur, Firmenreise oder eine massgeschneiderte Fahrt durch die Schweiz und Europa — unser Team steht Ihnen täglich von 09:00 bis 19:30 zur Verfügung. Anrufe und WhatsApp werden während der Geschäftszeit direkt beantwortet; ausserhalb dieser Zeiten antworten wir per E-Mail am nächsten Morgen.",
    ctaBook: "Online buchen", ctaWa: "Über WhatsApp",
    optionsTitle: "Wie möchten Sie uns erreichen?",
    phoneCard: { title: "Anruf", sub: "Mo–So · 09:00 – 19:30", cta: "Anrufen" },
    waCard: { title: "WhatsApp", sub: "Antwort in ~10 Min während der Geschäftszeit", cta: "Chatten" },
    emailCard: { title: "E-Mail", sub: "Antwort am nächsten Werktag", cta: "Schreiben" },
    urgentCard: { title: "Ausserhalb der Zeiten?", sub: "E-Mail senden — Antwort am nächsten Morgen", cta: "E-Mail senden" },
    formTitle: "Anfrage stellen", formSub: "Während der Geschäftszeit (09:00 – 19:30) antworten wir innerhalb von 2 Stunden. Ausserhalb: nächster Werktag.",
    f: { name: "Vollständiger Name", company: "Firma", email: "E-Mail", phone: "Telefon", preferred: "Bevorzugter Kanal", service: "Gewünschter Service", date: "Datum", time: "Uhrzeit", msg: "Nachricht" },
    channels: { phone: "Telefon", whatsapp: "WhatsApp", email: "E-Mail" },
    services: ["Flughafentransfer", "Business Chauffeur", "Executive Roadshow", "Chauffeur stundenweise", "Internationaler Transfer", "Hoteltransfer", "Privatveranstaltung", "Hochzeit", "Andere"],
    submit: "Anfrage senden", sending: "Wird gesendet…",
    success: "Danke.", successBody: "Ihre Anfrage wurde erfolgreich empfangen.",
    ref: "Referenznummer", callback: "Eine unserer Chauffeurinnen meldet sich in Kürze bei Ihnen.",
    corpTitle: "Corporate & Concierge",
    corpBody: "Für Hotels, Premium-Resorts, Reiseagenturen, Executive Assistants, Kanzleien, Privatbanken und Konzerne bieten wir dedizierte Rahmenverträge und monatliche Abrechnung.",
    corpTargets: ["Hotels & Resorts", "Reiseagenturen", "Executive Assistants", "Kanzleien", "Privatbanken", "Konzerne"],
    corpCta: "Firmenkonto eröffnen",
    whyTitle: "Warum Swiss AlpenLine?",
    why: [
      { i: BadgeCheck, t: "Gründerin & lizenzierte Chauffeurin", d: "16 Jahre chauffeur-Erfahrung · Fahrkategorien A · A1 · B · B1 · B121 · C · CE · diskretes Executive-Training nach internationalem Standard." },
      { i: Award, t: "Executive BYD Seal U", d: "Executive-SUV 2025 mit Ledersitzen, WLAN, Wasser & Ladegeräten an Bord." },
      { i: CreditCard, t: "Feste, transparente Preise", d: "Vollständiger Preis vor der Bestätigung. Keine versteckten Zuschläge." },
      { i: Globe2, t: "365 Tage · CH & Europa", d: "Täglich von 09:00 bis 19:30 in der Schweiz, Frankreich, Deutschland, Italien, Österreich und Liechtenstein." },
    ],
    availTitle: "Geschäftszeiten",
    avail24: "09:00 – 19:30", avail365: "7 Tage die Woche",
    availItems: ["Flughafentransfers", "Business Chauffeur", "Internationale Transfers"],
    responseTitle: "Antwortzeiten",
    responses: [
      { i: MessageCircle, l: "WhatsApp", v: "~10 Min · Geschäftszeit" },
      { i: Phone, l: "Telefon", v: "während der Geschäftszeit" },
      { i: Mail, l: "E-Mail", v: "nächster Werktag" },
    ],
    trustTitle: "Sicherheit & Vertrauen",
    trust: [
      { i: Lock, l: "SSL-verschlüsselte Website" },
      { i: CreditCard, l: "Sichere Stripe-Zahlungen" },
      { i: BadgeCheck, l: "Lizenzierte Chauffeurin" },
      { i: Award, l: "Executive Service" },
      { i: Globe2, l: "Schweizer Unternehmen" },
    ],
    faqTitle: "Häufige Fragen",
    faqs: [
      { q: "Kann ich heute noch buchen?", a: "Kurzfristige Anfragen werden während der Geschäftszeit (09:00 – 19:30) via WhatsApp innerhalb weniger Minuten geprüft. Nachts oder am Wochenende ausserhalb dieser Zeiten senden Sie bitte eine E-Mail — wir bestätigen am nächsten Morgen." },
      { q: "Bieten Sie internationale Transfers an?", a: "Ja — Schweiz ↔ Frankreich, Deutschland, Italien, Österreich, Liechtenstein. Grenzformalitäten inbegriffen." },
      { q: "Kann ich mit Haustier reisen?", a: "Ja. Bitte in der Buchung vermerken. Bevorzugt in einer Transportbox." },
      { q: "Kann ich einen Kindersitz anfordern?", a: "Ja — Babyschale, Kindersitz und Sitzerhöhung verfügbar. Bitte bei der Buchung angeben." },
      { q: "Überwachen Sie Flugverspätungen?", a: "Ja. Bei Flughafentransfers passen wir die Abholzeit automatisch an — kostenlose Wartezeit von 45 Minuten." },
      { q: "Kann ich meine Buchung ändern?", a: "Kostenlose Änderungen bis 48 Std vor Abholung. Danach gelten die Stornobedingungen (siehe AGB)." },
    ],
    mapTitle: "Unser Standort",
    hqAddress: "Luzern · Schweiz",
    openInMaps: "In Google Maps öffnen",
  },
  en: {
    kicker: "Contact · 09:00 – 19:30",
    title: "Let's plan your journey.",
    subtitle: "Airport transfer, executive chauffeur, corporate trip or a bespoke drive across Switzerland and Europe — our team is available daily from 09:00 to 19:30. Calls and WhatsApp are answered live during business hours; outside these hours we reply by email first thing the following morning.",
    ctaBook: "Book online", ctaWa: "Chat on WhatsApp",
    optionsTitle: "How would you like to reach us?",
    phoneCard: { title: "Call", sub: "Mon–Sun · 09:00 – 19:30", cta: "Call now" },
    waCard: { title: "WhatsApp", sub: "Reply within ~10 min · business hours", cta: "Start chat" },
    emailCard: { title: "Email", sub: "Reply next business day", cta: "Write us" },
    urgentCard: { title: "Outside business hours?", sub: "Send us an email — reply first thing next morning", cta: "Send email" },
    formTitle: "Send a request", formSub: "During business hours (09:00 – 19:30) we reply within 2 hours. Outside hours: next business day.",
    f: { name: "Full name", company: "Company", email: "Email", phone: "Phone", preferred: "Preferred channel", service: "Service required", date: "Date", time: "Time", msg: "Message" },
    channels: { phone: "Phone", whatsapp: "WhatsApp", email: "Email" },
    services: ["Airport transfer", "Business chauffeur", "Executive roadshow", "Chauffeur by the hour", "International transfer", "Hotel transfer", "Private event", "Wedding", "Other"],
    submit: "Send request", sending: "Sending…",
    success: "Thank you.", successBody: "Your request has been received successfully.",
    ref: "Reference number", callback: "One of our chauffeurs will be in touch shortly.",
    corpTitle: "Corporate & Concierge",
    corpBody: "For hotels, premium resorts, travel agencies, executive assistants, law firms, private banks and corporate groups we offer dedicated framework contracts and monthly invoicing.",
    corpTargets: ["Hotels & Resorts", "Travel agencies", "Executive assistants", "Law firms", "Private banks", "Corporate groups"],
    corpCta: "Open a corporate account",
    whyTitle: "Why Swiss AlpenLine?",
    why: [
      { i: BadgeCheck, t: "Female founder & licensed chauffeur", d: "16 years of chauffeured experience · driving categories A · A1 · B · B1 · B121 · C · CE · discreet executive training to international standard." },
      { i: Award, t: "Executive BYD Seal U", d: "2025 executive SUV with leather seats, Wi-Fi, water & chargers on board." },
      { i: CreditCard, t: "Fixed, transparent pricing", d: "Full price before confirmation. No hidden surcharges." },
      { i: Globe2, t: "365 days · CH & Europe", d: "Available daily 09:00 – 19:30 across Switzerland, France, Germany, Italy, Austria, Liechtenstein." },
    ],
    availTitle: "Business hours",
    avail24: "09:00 – 19:30", avail365: "7 days a week",
    availItems: ["Airport transfers", "Business chauffeur", "International transfers"],
    responseTitle: "Response times",
    responses: [
      { i: MessageCircle, l: "WhatsApp", v: "~10 min · business hours" },
      { i: Phone, l: "Phone", v: "live during business hours" },
      { i: Mail, l: "Email", v: "next business day" },
    ],
    trustTitle: "Security & Trust",
    trust: [
      { i: Lock, l: "SSL-encrypted website" },
      { i: CreditCard, l: "Secure Stripe payments" },
      { i: BadgeCheck, l: "Licensed chauffeur" },
      { i: Award, l: "Executive service" },
      { i: Globe2, l: "Swiss-registered company" },
    ],
    faqTitle: "Frequently asked",
    faqs: [
      { q: "Can I book for today?", a: "Same-day requests are handled live on WhatsApp during business hours (09:00 – 19:30). Outside these hours please email us and we'll confirm first thing the next morning." },
      { q: "Do you offer international transfers?", a: "Yes — Switzerland ↔ France, Germany, Italy, Austria, Liechtenstein. Border formalities included." },
      { q: "Can I travel with a pet?", a: "Yes. Please mention it in the booking. Preferably in a carrier." },
      { q: "Can I request a child seat?", a: "Yes — baby carrier, child seat and booster available. Please state at booking." },
      { q: "Do you track flight delays?", a: "Yes. For airport transfers we adjust pickup automatically — 45 min free waiting time." },
      { q: "Can I change my booking?", a: "Free changes up to 48 h before pickup. Cancellation terms apply afterwards (see T&Cs)." },
    ],
    mapTitle: "Our location",
    hqAddress: "Lucerne · Switzerland",
    openInMaps: "Open in Google Maps",
  },
  pt: {
    kicker: "Contacto · 09:00 – 19:30",
    title: "Vamos planear a sua viagem.",
    subtitle: "Transfer aeroporto, chauffeur executivo, viagem corporativa ou um trajeto à medida pela Suíça e Europa — a nossa equipa está disponível diariamente das 09:00 às 19:30. Chamadas e WhatsApp são respondidas em direto durante o horário; fora deste, respondemos por e-mail logo pela manhã seguinte.",
    ctaBook: "Reservar online", ctaWa: "WhatsApp",
    optionsTitle: "Como prefere contactar-nos?",
    phoneCard: { title: "Chamada", sub: "Seg–Dom · 09:00 – 19:30", cta: "Ligar" },
    waCard: { title: "WhatsApp", sub: "Resposta em ~10 min · horário laboral", cta: "Iniciar chat" },
    emailCard: { title: "E-mail", sub: "Resposta no próximo dia útil", cta: "Escrever" },
    urgentCard: { title: "Fora do horário laboral?", sub: "Envie e-mail — resposta na manhã seguinte", cta: "Enviar e-mail" },
    formTitle: "Enviar pedido", formSub: "Durante o horário (09:00 – 19:30) respondemos em até 2 horas. Fora do horário: no próximo dia útil.",
    f: { name: "Nome completo", company: "Empresa", email: "E-mail", phone: "Telefone", preferred: "Canal preferido", service: "Serviço pretendido", date: "Data", time: "Hora", msg: "Mensagem" },
    channels: { phone: "Telefone", whatsapp: "WhatsApp", email: "E-mail" },
    services: ["Transfer aeroporto", "Chauffeur business", "Roadshow executivo", "Chauffeur à hora", "Transfer internacional", "Transfer hotel", "Evento privado", "Casamento", "Outro"],
    submit: "Enviar pedido", sending: "A enviar…",
    success: "Obrigado.", successBody: "O seu pedido foi recebido com sucesso.",
    ref: "Número de referência", callback: "Uma das nossas chauffeurs entrará em contacto em breve.",
    corpTitle: "Corporate & Concierge",
    corpBody: "Para hotéis, resorts premium, agências de viagem, executive assistants, escritórios de advogados, bancos privados e grupos empresariais oferecemos contratos-quadro dedicados e faturação mensal.",
    corpTargets: ["Hotéis & Resorts", "Agências de viagem", "Executive assistants", "Escritórios de advogados", "Bancos privados", "Grupos empresariais"],
    corpCta: "Abrir conta corporate",
    whyTitle: "Porquê a Swiss AlpenLine?",
    why: [
      { i: BadgeCheck, t: "Fundadora e chauffeur licenciada", d: "16 anos de experiência como chauffeur · categorias A · A1 · B · B1 · B121 · C · CE · formação executiva discreta de nível internacional." },
      { i: Award, t: "Executive BYD Seal U", d: "SUV executivo 2025 com bancos em pele, Wi-Fi, água e carregadores a bordo." },
      { i: CreditCard, t: "Preços fixos e transparentes", d: "Preço total antes da confirmação. Sem taxas ocultas." },
      { i: Globe2, t: "365 dias · CH & Europa", d: "Disponíveis diariamente das 09:00 às 19:30 na Suíça, França, Alemanha, Itália, Áustria e Liechtenstein." },
    ],
    availTitle: "Horário laboral",
    avail24: "09:00 – 19:30", avail365: "7 dias por semana",
    availItems: ["Transfers aeroporto", "Chauffeur business", "Transfers internacionais"],
    responseTitle: "Tempos de resposta",
    responses: [
      { i: MessageCircle, l: "WhatsApp", v: "~10 min · horário laboral" },
      { i: Phone, l: "Telefone", v: "em direto durante o horário" },
      { i: Mail, l: "E-mail", v: "próximo dia útil" },
    ],
    trustTitle: "Segurança & Confiança",
    trust: [
      { i: Lock, l: "Website com encriptação SSL" },
      { i: CreditCard, l: "Pagamentos Stripe seguros" },
      { i: BadgeCheck, l: "Chauffeur licenciada" },
      { i: Award, l: "Serviço executivo" },
      { i: Globe2, l: "Empresa suíça" },
    ],
    faqTitle: "Perguntas frequentes",
    faqs: [
      { q: "Posso reservar para hoje?", a: "Pedidos para o próprio dia são tratados em direto via WhatsApp durante o horário (09:00 – 19:30). Fora deste horário envie e-mail — confirmamos logo pela manhã seguinte." },
      { q: "Fazem transfers internacionais?", a: "Sim — Suíça ↔ França, Alemanha, Itália, Áustria, Liechtenstein. Formalidades fronteiriças incluídas." },
      { q: "Posso viajar com o meu animal?", a: "Sim. Indique-o na reserva. Preferencialmente em transportadora." },
      { q: "Posso pedir cadeira para criança?", a: "Sim — ovo, cadeira e assento elevatório disponíveis. Indique na reserva." },
      { q: "Monitorizam atrasos de voo?", a: "Sim. Nos transfers aeroporto ajustamos automaticamente a hora — 45 min de espera gratuita." },
      { q: "Posso alterar a reserva?", a: "Alterações gratuitas até 48 h antes do embarque. Depois aplicam-se as condições de cancelamento (ver T&C)." },
    ],
    mapTitle: "A nossa localização",
    hqAddress: "Luzern · Suíça",
    openInMaps: "Abrir no Google Maps",
  },
  fr: {
    kicker: "Contact · 09:00 – 19:30",
    title: "Planifions votre trajet.",
    subtitle: "Transfert aéroport, chauffeur exécutif, voyage d'affaires ou trajet sur mesure à travers la Suisse et l'Europe — notre équipe est disponible tous les jours de 09:00 à 19:30. Les appels et WhatsApp sont pris en direct pendant les heures ouvrées ; en dehors, nous répondons par e-mail dès le lendemain matin.",
    ctaBook: "Réserver en ligne", ctaWa: "WhatsApp",
    optionsTitle: "Comment souhaitez-vous nous joindre ?",
    phoneCard: { title: "Appel", sub: "Lun–Dim · 09:00 – 19:30", cta: "Appeler" },
    waCard: { title: "WhatsApp", sub: "Réponse en ~10 min · heures ouvrées", cta: "Démarrer" },
    emailCard: { title: "E-mail", sub: "Réponse le prochain jour ouvré", cta: "Écrire" },
    urgentCard: { title: "Hors heures ouvrées ?", sub: "Envoyez un e-mail — réponse dès le lendemain matin", cta: "Envoyer un e-mail" },
    formTitle: "Envoyer une demande", formSub: "Pendant les heures ouvrées (09:00 – 19:30) nous répondons sous 2 heures. En dehors : prochain jour ouvré.",
    f: { name: "Nom complet", company: "Entreprise", email: "E-mail", phone: "Téléphone", preferred: "Canal préféré", service: "Service souhaité", date: "Date", time: "Heure", msg: "Message" },
    channels: { phone: "Téléphone", whatsapp: "WhatsApp", email: "E-mail" },
    services: ["Transfert aéroport", "Chauffeur business", "Roadshow exécutif", "Chauffeur à l'heure", "Transfert international", "Transfert hôtel", "Événement privé", "Mariage", "Autre"],
    submit: "Envoyer la demande", sending: "Envoi…",
    success: "Merci.", successBody: "Votre demande a bien été reçue.",
    ref: "Numéro de référence", callback: "L'une de nos chauffeuses vous contactera sous peu.",
    corpTitle: "Corporate & Concierge",
    corpBody: "Pour hôtels, resorts premium, agences de voyage, executive assistants, cabinets d'avocats, banques privées et groupes corporate — contrats-cadres dédiés et facturation mensuelle.",
    corpTargets: ["Hôtels & Resorts", "Agences de voyage", "Executive assistants", "Cabinets d'avocats", "Banques privées", "Groupes corporate"],
    corpCta: "Ouvrir un compte entreprise",
    whyTitle: "Pourquoi Swiss AlpenLine ?",
    why: [
      { i: BadgeCheck, t: "Fondatrice & chauffeuse licenciée", d: "16 ans d'expérience de chauffeur · catégories A · A1 · B · B1 · B121 · C · CE · formation exécutive discrète de standard international." },
      { i: Award, t: "Executive BYD Seal U", d: "SUV executive 2025 avec sièges en cuir, Wi-Fi, eau et chargeurs à bord." },
      { i: CreditCard, t: "Tarifs fixes et transparents", d: "Prix total avant confirmation. Aucun supplément caché." },
      { i: Globe2, t: "365 jours · CH & Europe", d: "Disponibles tous les jours de 09:00 à 19:30 en Suisse, France, Allemagne, Italie, Autriche et Liechtenstein." },
    ],
    availTitle: "Heures ouvrées",
    avail24: "09:00 – 19:30", avail365: "7 jours sur 7",
    availItems: ["Transferts aéroport", "Chauffeur business", "Transferts internationaux"],
    responseTitle: "Temps de réponse",
    responses: [
      { i: MessageCircle, l: "WhatsApp", v: "~10 min · heures ouvrées" },
      { i: Phone, l: "Téléphone", v: "en direct pendant les heures" },
      { i: Mail, l: "E-mail", v: "prochain jour ouvré" },
    ],
    trustTitle: "Sécurité & Confiance",
    trust: [
      { i: Lock, l: "Site web chiffré SSL" },
      { i: CreditCard, l: "Paiements Stripe sécurisés" },
      { i: BadgeCheck, l: "Chauffeuse licenciée" },
      { i: Award, l: "Service exécutif" },
      { i: Globe2, l: "Entreprise suisse" },
    ],
    faqTitle: "Questions fréquentes",
    faqs: [
      { q: "Puis-je réserver pour aujourd'hui ?", a: "Les demandes pour le jour même sont traitées en direct sur WhatsApp pendant les heures ouvrées (09:00 – 19:30). En dehors de ces heures, envoyez un e-mail — nous confirmons dès le lendemain matin." },
      { q: "Proposez-vous des transferts internationaux ?", a: "Oui — Suisse ↔ France, Allemagne, Italie, Autriche, Liechtenstein. Formalités frontières incluses." },
      { q: "Puis-je voyager avec un animal ?", a: "Oui. Merci de l'indiquer dans la réservation. De préférence en caisse de transport." },
      { q: "Puis-je demander un siège enfant ?", a: "Oui — coque bébé, siège enfant et rehausseur disponibles. À préciser à la réservation." },
      { q: "Suivez-vous les retards de vol ?", a: "Oui. Pour les transferts aéroport, la prise en charge est ajustée automatiquement — 45 min d'attente gratuites." },
      { q: "Puis-je modifier ma réservation ?", a: "Modifications gratuites jusqu'à 48 h avant la prise en charge. Conditions d'annulation ensuite (voir CGV)." },
    ],
    mapTitle: "Notre emplacement",
    hqAddress: "Lucerne · Suisse",
    openInMaps: "Ouvrir dans Google Maps",
  },
  it: {
    kicker: "Contatti · 09:00 – 19:30",
    title: "Pianifichiamo il vostro viaggio.",
    subtitle: "Transfer aeroporto, chauffeur executive, viaggio aziendale o un percorso su misura tra Svizzera ed Europa — il nostro team è disponibile ogni giorno dalle 09:00 alle 19:30. Chiamate e WhatsApp vengono presi in diretta durante l'orario; al di fuori rispondiamo via e-mail la mattina successiva.",
    ctaBook: "Prenota online", ctaWa: "WhatsApp",
    optionsTitle: "Come preferite contattarci?",
    phoneCard: { title: "Chiamata", sub: "Lun–Dom · 09:00 – 19:30", cta: "Chiama" },
    waCard: { title: "WhatsApp", sub: "Risposta in ~10 min · orario d'ufficio", cta: "Avvia chat" },
    emailCard: { title: "E-mail", sub: "Risposta il giorno lavorativo successivo", cta: "Scrivi" },
    urgentCard: { title: "Fuori orario?", sub: "Inviate una e-mail — risposta la mattina successiva", cta: "Invia e-mail" },
    formTitle: "Invia richiesta", formSub: "Durante l'orario (09:00 – 19:30) rispondiamo entro 2 ore. Fuori orario: giorno lavorativo successivo.",
    f: { name: "Nome completo", company: "Azienda", email: "E-mail", phone: "Telefono", preferred: "Canale preferito", service: "Servizio richiesto", date: "Data", time: "Ora", msg: "Messaggio" },
    channels: { phone: "Telefono", whatsapp: "WhatsApp", email: "E-mail" },
    services: ["Transfer aeroporto", "Chauffeur business", "Roadshow executive", "Chauffeur ad ore", "Transfer internazionale", "Transfer hotel", "Evento privato", "Matrimonio", "Altro"],
    submit: "Invia richiesta", sending: "Invio…",
    success: "Grazie.", successBody: "La vostra richiesta è stata ricevuta con successo.",
    ref: "Numero di riferimento", callback: "Una delle nostre chauffeur vi contatterà a breve.",
    corpTitle: "Corporate & Concierge",
    corpBody: "Per hotel, resort premium, agenzie viaggi, executive assistant, studi legali, banche private e gruppi corporate offriamo contratti quadro dedicati e fatturazione mensile.",
    corpTargets: ["Hotel & Resort", "Agenzie viaggi", "Executive assistant", "Studi legali", "Banche private", "Gruppi corporate"],
    corpCta: "Aprire un account business",
    whyTitle: "Perché Swiss AlpenLine?",
    why: [
      { i: BadgeCheck, t: "Fondatrice e chauffeur autorizzata", d: "16 anni di esperienza come chauffeur · categorie A · A1 · B · B1 · B121 · C · CE · formazione executive discreta di livello internazionale." },
      { i: Award, t: "Executive BYD Seal U", d: "SUV executive 2025 con sedili in pelle, Wi-Fi, acqua e caricatori a bordo." },
      { i: CreditCard, t: "Prezzi fissi e trasparenti", d: "Prezzo totale prima della conferma. Nessun supplemento nascosto." },
      { i: Globe2, t: "365 giorni · CH & Europa", d: "Disponibili ogni giorno dalle 09:00 alle 19:30 in Svizzera, Francia, Germania, Italia, Austria e Liechtenstein." },
    ],
    availTitle: "Orari d'ufficio",
    avail24: "09:00 – 19:30", avail365: "7 giorni su 7",
    availItems: ["Transfer aeroporto", "Chauffeur business", "Transfer internazionali"],
    responseTitle: "Tempi di risposta",
    responses: [
      { i: MessageCircle, l: "WhatsApp", v: "~10 min · orario d'ufficio" },
      { i: Phone, l: "Telefono", v: "in diretta durante l'orario" },
      { i: Mail, l: "E-mail", v: "giorno lavorativo successivo" },
    ],
    trustTitle: "Sicurezza & Fiducia",
    trust: [
      { i: Lock, l: "Sito con crittografia SSL" },
      { i: CreditCard, l: "Pagamenti Stripe sicuri" },
      { i: BadgeCheck, l: "Chauffeur autorizzata" },
      { i: Award, l: "Servizio executive" },
      { i: Globe2, l: "Azienda svizzera" },
    ],
    faqTitle: "Domande frequenti",
    faqs: [
      { q: "Posso prenotare per oggi?", a: "Le richieste in giornata vengono gestite in diretta su WhatsApp durante l'orario (09:00 – 19:30). Fuori orario inviate una e-mail — confermiamo la mattina successiva." },
      { q: "Offrite transfer internazionali?", a: "Sì — Svizzera ↔ Francia, Germania, Italia, Austria, Liechtenstein. Formalità di confine incluse." },
      { q: "Posso viaggiare con un animale?", a: "Sì. Indicatelo nella prenotazione. Preferibilmente in trasportino." },
      { q: "Posso richiedere un seggiolino?", a: "Sì — ovetto, seggiolino e rialzo disponibili. Da indicare in prenotazione." },
      { q: "Monitorate i ritardi dei voli?", a: "Sì. Per i transfer aeroportuali regoliamo automaticamente l'orario di pickup — 45 min di attesa gratuiti." },
      { q: "Posso modificare la prenotazione?", a: "Modifiche gratuite fino a 48 h prima. Poi si applicano le condizioni di cancellazione (vedi T&C)." },
    ],
    mapTitle: "La nostra sede",
    hqAddress: "Lucerna · Svizzera",
    openInMaps: "Apri su Google Maps",
  },
};
const getT = (l) => T[l] || T.de;

const OptionCard = ({ Icon, title, sub, cta, href, onClick, testId }) => {
  const Tag = href ? "a" : "button";
  return (
    <Tag
      href={href} onClick={onClick}
      target={href && href.startsWith("http") ? "_blank" : undefined}
      rel={href && href.startsWith("http") ? "noreferrer" : undefined}
      data-testid={testId}
      className="group block w-full text-left border border-brand-border bg-brand-surface hover:border-bronze p-6 transition-all duration-500 hover:-translate-y-1"
    >
      <div className="w-11 h-11 border border-brand-border rounded-full flex items-center justify-center mb-5 transition-colors group-hover:border-bronze"
           style={{ color: GOLD }}>
        <Icon size={18} />
      </div>
      <p className="text-brand-platinum text-lg font-light mb-1">{title}</p>
      <p className="text-brand-silver text-xs mb-6">{sub}</p>
      <span className="text-[10px] tracking-widest2 uppercase text-brand-platinum flex items-center gap-2 group-hover:gap-3 transition-all">
        {cta} <ArrowRight size={12} />
      </span>
    </Tag>
  );
};

const Contacto = () => {
  const { lang } = useLang();
  const c = getT(lang);
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: "", company: "", email: "", phone: "",
    preferred: "whatsapp", service: c.services[0],
    date: "", time: "", message: "",
  });
  const [sending, setSending] = useState(false);
  const [ref, setRef] = useState(null);
  const [openFaq, setOpenFaq] = useState(0);
  const [detecting, setDetecting] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const detectLabels = {
    de: { btn: "Aus Nachricht automatisch erkennen", short: "Nachricht muss mindestens 20 Zeichen haben.", none: "Keine Reiseabsicht in der Nachricht erkannt." },
    en: { btn: "Auto-detect from message", short: "Message must be at least 20 characters.", none: "No booking intent detected in your message." },
    pt: { btn: "Detectar automaticamente da mensagem", short: "A mensagem tem de ter pelo menos 20 caracteres.", none: "Nenhuma viagem detectada na mensagem." },
    fr: { btn: "Détecter automatiquement depuis le message", short: "Le message doit avoir au moins 20 caractères.", none: "Aucune intention de réservation détectée dans le message." },
    it: { btn: "Rileva automaticamente dal messaggio", short: "Il messaggio deve avere almeno 20 caratteri.", none: "Nessun viaggio rilevato nel messaggio." },
  };
  const dt = detectLabels[lang] || detectLabels.de;

  const buildBookingUrl = (data) => {
    const p = new URLSearchParams();
    if (data.origin) p.set("origin", data.origin);
    if (data.destination) p.set("destination", data.destination);
    if (data.date && data.time) p.set("when", `${data.date}T${data.time}`);
    else if (data.date) p.set("when", `${data.date}T09:00`);
    if (data.service) p.set("service", data.service);
    if (data.passengers) p.set("passengers", String(data.passengers));
    if (data.luggage) p.set("luggage", String(data.luggage));
    if (data.flight_number) p.set("flight", data.flight_number);
    return `/reservar?${p.toString()}`;
  };

  const autoDetect = async () => {
    if (form.message.trim().length < 20) { toast.error(dt.short); return; }
    setDetecting(true);
    try {
      const r = await axios.post(`${API}/concierge/extract`, { text: form.message, lang });
      const d = r.data || {};
      if (d.confidence >= 0.5 && (d.origin || d.destination)) {
        navigate(buildBookingUrl(d));
      } else {
        toast.error(dt.none);
      }
    } catch {
      toast.error("Sophie · unavailable");
    }
    setDetecting(false);
  };

  useEffect(() => {
    document.title = `Swiss AlpenLine | ${c.kicker}`;
  }, [c.kicker]);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) { toast.error("Please fill required fields"); return; }
    setSending(true);
    try {
      const reference = genRef();
      await axios.post(`${API}/contact`, {
        name: form.name,
        email: form.email,
        subject: `[${form.service}] ${form.company ? form.company + " · " : ""}${form.preferred}`,
        message: `${form.message}\n\n---\nPhone: ${form.phone}\nPreferred: ${form.preferred}\nDate: ${form.date || "—"} · Time: ${form.time || "—"}\nRef: ${reference}`,
      });
      setRef(reference);
    } catch { toast.error("Failed to send"); }
    setSending(false);
  };

  const cls = "w-full bg-brand-bg border border-brand-border text-brand-platinum px-4 py-3 text-sm focus:border-bronze transition-colors focus:outline-none";
  const label = "text-[10px] tracking-widest2 uppercase text-brand-silver mb-2 block";

  return (
    <main className="pt-32 pb-24 bg-brand-bg">
      {/* HERO */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.15fr_1fr] gap-12 lg:gap-20 items-center">
          <Reveal>
            <p className="text-[11px] tracking-[0.25em] uppercase mb-5" style={{ color: GOLD }}>
              — {c.kicker}
            </p>
            <h1 className="display text-4xl md:text-6xl lg:text-7xl text-brand-platinum leading-[1.05] tracking-tight mb-6">
              {c.title}
            </h1>
            <p className="text-brand-silver text-base leading-relaxed max-w-xl mb-10">{c.subtitle}</p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Link to="/reservar" data-testid="hero-cta-book" className="btn-pill btn-pill--solid">
                {c.ctaBook} <ArrowRight size={14} />
              </Link>
              <a href={WA_URL} target="_blank" rel="noreferrer" data-testid="hero-cta-wa" className="btn-pill">
                <MessageCircle size={14} /> {c.ctaWa}
              </a>
            </div>
          </Reveal>

          <Reveal delay={150}>
            <div className="aspect-[4/3] border border-brand-border overflow-hidden">
              <img src={IMG_HERO} alt="Swiss AlpenLine chauffeur" className="w-full h-full object-cover kenburns" />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 4 OPTION CARDS */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <Reveal>
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.optionsTitle}</p>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { Icon: Phone, title: c.phoneCard.title, sub: c.phoneCard.sub, cta: c.phoneCard.cta, href: `tel:${PHONE}`, testId: "opt-phone" },
            { Icon: MessageCircle, title: c.waCard.title, sub: c.waCard.sub, cta: c.waCard.cta, href: WA_URL, testId: "opt-wa" },
            { Icon: Mail, title: c.emailCard.title, sub: c.emailCard.sub, cta: c.emailCard.cta, href: `mailto:${EMAIL}`, testId: "opt-email" },
            { Icon: Zap, title: c.urgentCard.title, sub: c.urgentCard.sub, cta: c.urgentCard.cta, href: WA_URL + "?text=" + encodeURIComponent("Urgent — need chauffeur today"), testId: "opt-urgent" },
          ].map((o, i) => (
            <Reveal key={o.testId} delay={i * 80}>
              <OptionCard {...o} />
            </Reveal>
          ))}
        </div>
      </section>

      {/* SOPHIE — AI Concierge */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24" data-testid="section-sophie">
        <Reveal>
          <SophieConcierge lang={lang} />
        </Reveal>
      </section>

      {/* FORM + RESPONSE TIMES + AVAILABILITY */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="grid grid-cols-1 lg:grid-cols-[1.5fr_1fr] gap-10 lg:gap-16 items-start">
          <Reveal>
            {ref ? (
              <div className="border border-bronze/50 bg-bronze/[0.05] p-12 text-center fadeUp">
                <CheckCircle2 size={48} className="mx-auto mb-6" style={{ color: GOLD }} />
                <p className="display text-3xl md:text-4xl text-brand-platinum mb-3">{c.success}</p>
                <p className="text-brand-silver text-sm mb-8">{c.successBody}</p>
                <div className="inline-block border border-brand-border bg-brand-surface px-6 py-4 mb-6">
                  <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-1">{c.ref}</p>
                  <p className="text-brand-platinum text-lg tabular-nums font-mono" data-testid="contact-ref">{ref}</p>
                </div>
                <p className="text-brand-silver text-xs">{c.callback}</p>
              </div>
            ) : (
              <form onSubmit={submit} data-testid="contact-form" className="border border-brand-border bg-brand-surface p-8 md:p-10">
                <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-2">— {c.formTitle}</p>
                <p className="text-brand-silver text-sm mb-8">{c.formSub}</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <label className="md:col-span-2 block">
                    <span className={label}>{c.f.name} *</span>
                    <input value={form.name} onChange={(e) => set("name", e.target.value)} required
                           data-testid="contact-name" className={cls} />
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.company}</span>
                    <input value={form.company} onChange={(e) => set("company", e.target.value)}
                           data-testid="contact-company" className={cls} />
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.email} *</span>
                    <input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} required
                           data-testid="contact-email-input" className={cls} />
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.phone}</span>
                    <input value={form.phone} onChange={(e) => set("phone", e.target.value)}
                           data-testid="contact-phone" className={cls} />
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.preferred}</span>
                    <select value={form.preferred} onChange={(e) => set("preferred", e.target.value)}
                            data-testid="contact-preferred" className={cls}>
                      <option value="phone">{c.channels.phone}</option>
                      <option value="whatsapp">{c.channels.whatsapp}</option>
                      <option value="email">{c.channels.email}</option>
                    </select>
                  </label>
                  <label className="md:col-span-2 block">
                    <span className={label}>{c.f.service}</span>
                    <select value={form.service} onChange={(e) => set("service", e.target.value)}
                            data-testid="contact-service" className={cls}>
                      {c.services.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.date}</span>
                    <input type="date" value={form.date} onChange={(e) => set("date", e.target.value)}
                           data-testid="contact-date" className={cls} />
                  </label>
                  <label className="block">
                    <span className={label}>{c.f.time}</span>
                    <input type="time" value={form.time} onChange={(e) => set("time", e.target.value)}
                           data-testid="contact-time" className={cls} />
                  </label>
                  <label className="md:col-span-2 block">
                    <div className="flex items-center justify-between mb-2">
                      <span className={label + " mb-0"}>{c.f.msg} *</span>
                      <button
                        type="button"
                        onClick={autoDetect}
                        disabled={detecting || form.message.trim().length < 20}
                        data-testid="contact-autodetect"
                        className="text-[10px] tracking-widest2 uppercase flex items-center gap-2 border border-brand-border px-3 py-1.5 hover:border-bronze transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
                        style={{ color: GOLD }}
                      >
                        {detecting ? <Loader2 size={11} className="animate-spin" /> : <Sparkles size={11} />}
                        {dt.btn}
                      </button>
                    </div>
                    <textarea rows={5} value={form.message} onChange={(e) => set("message", e.target.value)} required
                              data-testid="contact-message" className={cls + " resize-none"} />
                  </label>
                </div>
                <button type="submit" disabled={sending} data-testid="contact-submit"
                        className="btn-pill btn-pill--solid mt-8 w-full sm:w-auto justify-center">
                  {sending ? <><Loader2 size={14} className="animate-spin" /> {c.sending}</> : <>{c.submit} <Send size={14} /></>}
                </button>
              </form>
            )}
          </Reveal>

          <aside className="space-y-6">
            <Reveal delay={150}>
              <div className="border border-brand-border p-6">
                <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.responseTitle}</p>
                <ul className="space-y-4">
                  {c.responses.map((r) => (
                    <li key={r.l} className="flex items-center gap-3">
                      <span className="w-8 h-8 rounded-full border border-brand-border flex items-center justify-center shrink-0" style={{ color: GOLD }}>
                        <r.i size={13} />
                      </span>
                      <div className="flex-1">
                        <p className="text-brand-platinum text-sm">{r.l}</p>
                        <p className="text-brand-silver text-xs">{r.v}</p>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
            <Reveal delay={200}>
              <div className="border border-brand-border p-6" style={{ background: "rgba(176,141,47,0.04)" }}>
                <p className="text-[10px] tracking-widest2 uppercase mb-4" style={{ color: GOLD }}>— {c.availTitle}</p>
                <p className="display text-3xl text-brand-platinum mb-1">{c.avail24}</p>
                <p className="text-brand-silver text-sm mb-5">{c.avail365}</p>
                <ul className="space-y-2 text-xs text-brand-silver">
                  {c.availItems.map((it) => (
                    <li key={it} className="flex items-center gap-2">
                      <span className="w-1 h-1 rounded-full" style={{ background: GOLD }} />
                      {it}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          </aside>
        </div>
      </section>

      {/* WHY CHOOSE US */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <Reveal><p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.whyTitle}</p></Reveal>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
          {c.why.map((w, i) => (
            <Reveal key={w.t} delay={i * 80}>
              <div className="border border-brand-border bg-brand-surface p-6 h-full">
                <w.i size={22} className="mb-5" style={{ color: GOLD }} />
                <p className="text-brand-platinum text-lg font-light mb-3">{w.t}</p>
                <p className="text-brand-silver text-xs leading-relaxed">{w.d}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* CORPORATE */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <div className="border border-brand-border p-10 md:p-16 grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-10 items-center"
             style={{ background: "linear-gradient(135deg, rgba(176,141,47,0.06), transparent 60%)" }}>
          <Reveal>
            <p className="text-[10px] tracking-widest2 uppercase mb-4" style={{ color: GOLD }}>— Corporate</p>
            <h2 className="display text-3xl md:text-5xl text-brand-platinum leading-tight mb-6">{c.corpTitle}</h2>
            <p className="text-brand-silver text-sm leading-relaxed mb-8">{c.corpBody}</p>
            <a href={`mailto:${EMAIL}?subject=Corporate account request`} data-testid="corp-cta" className="btn-pill btn-pill--solid">
              <Building2 size={14} /> {c.corpCta} <ArrowRight size={14} />
            </a>
          </Reveal>
          <Reveal delay={150}>
            <ul className="grid grid-cols-2 gap-3">
              {c.corpTargets.map((t) => (
                <li key={t} className="border border-brand-border p-4 text-sm text-brand-silver flex items-center gap-2">
                  <Sparkles size={11} style={{ color: GOLD }} /> {t}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </section>

      {/* FAQ */}
      <section className="max-w-[900px] mx-auto px-6 lg:px-10 mb-24">
        <Reveal><p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.faqTitle}</p></Reveal>
        <div className="divide-y divide-brand-border border-y border-brand-border">
          {c.faqs.map((f, i) => (
            <div key={f.q} className="py-1">
              <button type="button"
                      onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                      data-testid={`faq-${i}`}
                      className="w-full text-left py-5 flex items-center justify-between gap-4 group">
                <span className="text-brand-platinum text-base md:text-lg">{f.q}</span>
                <ChevronDown size={16} className={`text-brand-silver shrink-0 transition-transform ${openFaq === i ? "rotate-180" : ""}`} style={{ color: openFaq === i ? GOLD : undefined }} />
              </button>
              {openFaq === i && (
                <div className="pb-6 text-brand-silver text-sm leading-relaxed fadeUp">{f.a}</div>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* MAP */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10 mb-24">
        <Reveal>
          <p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.mapTitle}</p>
          <div className="border border-brand-border overflow-hidden">
            <iframe
              title="Swiss AlpenLine HQ"
              src="https://www.openstreetmap.org/export/embed.html?bbox=8.24%2C47.02%2C8.38%2C47.09&layer=mapnik&marker=47.05017%2C8.30934"
              width="100%" height="360" style={{ border: 0, filter: "grayscale(70%) brightness(0.75) contrast(1.15)" }}
              loading="lazy" referrerPolicy="no-referrer-when-downgrade"
              allowFullScreen
            />
          </div>
          <a href="https://www.google.com/maps/dir/?api=1&destination=Luzern,Switzerland"
             target="_blank" rel="noreferrer"
             className="btn-pill mt-4 inline-flex" data-testid="open-in-maps">
            <MapPin size={12} /> {c.openInMaps} <ArrowRight size={12} />
          </a>
        </Reveal>
      </section>

      {/* TRUST */}
      <section className="max-w-[1400px] mx-auto px-6 lg:px-10">
        <Reveal><p className="text-[10px] tracking-widest2 uppercase text-brand-silver mb-4">— {c.trustTitle}</p></Reveal>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          {c.trust.map((t, i) => (
            <Reveal key={t.l} delay={i * 60}>
              <div className="border border-brand-border p-5 text-center">
                <t.i size={20} className="mx-auto mb-3" style={{ color: GOLD }} />
                <p className="text-brand-silver text-xs leading-relaxed">{t.l}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* Floating WhatsApp */}
      <a href={WA_URL} target="_blank" rel="noreferrer"
         data-testid="floating-wa"
         className="fixed bottom-6 right-6 z-40 flex items-center gap-3 bg-[#25D366] text-white px-5 py-3 rounded-full shadow-2xl hover:scale-105 transition-transform">
        <MessageCircle size={18} />
        <span className="text-sm">WhatsApp · 09:00 – 19:30</span>
      </a>

      <style>{`
        .fadeUp { animation: fadeUp 0.55s cubic-bezier(0.16, 1, 0.3, 1) both; }
        @keyframes fadeUp { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: none; } }
      `}</style>
    </main>
  );
};

export default Contacto;
